import React from 'react';
import Icon from 'components/AppIcon';

const DocumentTable = ({
  documents,
  selectedDocuments,
  viewMode,
  onDocumentSelect,
  onSelectAll,
  onPreview,
  onShare,
  onVersionHistory,
  getFileIcon,
  getStatusColor
}) => {
  const formatFileSize = (size) => {
    return size;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  if (viewMode === 'grid') {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {documents.map(document => (
          <div
            key={document.id}
            className={`bg-surface border border-border rounded-lg p-4 hover:shadow-card transition-smooth cursor-pointer ${
              selectedDocuments.includes(document.id) ? 'ring-2 ring-primary bg-primary-50' : ''
            }`}
            onClick={() => onDocumentSelect(document.id)}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedDocuments.includes(document.id)}
                  onChange={() => onDocumentSelect(document.id)}
                  className="mr-3 rounded border-border focus:ring-primary"
                  onClick={(e) => e.stopPropagation()}
                />
                <Icon name={getFileIcon(document.type)} size={24} className="text-primary" />
              </div>
              
              <div className="flex items-center gap-1">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreview(document);
                  }}
                  className="p-1 hover:bg-secondary-100 rounded transition-smooth"
                  title="Preview"
                >
                  <Icon name="Eye" size={14} />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare(document);
                  }}
                  className="p-1 hover:bg-secondary-100 rounded transition-smooth"
                  title="Share"
                >
                  <Icon name="Share2" size={14} />
                </button>
                <button className="p-1 hover:bg-secondary-100 rounded transition-smooth">
                  <Icon name="MoreVertical" size={14} />
                </button>
              </div>
            </div>
            
            <h4 className="font-medium text-text-primary text-sm mb-2 line-clamp-2">
              {document.name}
            </h4>
            
            <div className="space-y-2 text-xs text-text-secondary">
              <div className="flex justify-between">
                <span>Size:</span>
                <span>{formatFileSize(document.size)}</span>
              </div>
              <div className="flex justify-between">
                <span>Modified:</span>
                <span>{formatDate(document.lastModified)}</span>
              </div>
              <div className="flex justify-between">
                <span>Version:</span>
                <span>{document.version}</span>
              </div>
            </div>
            
            <div className="mt-3 flex items-center justify-between">
              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(document.status)}`}>
                {document.status}
              </span>
              {document.sharedWith.length > 0 && (
                <Icon name="Users" size={12} className="text-text-secondary" />
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-secondary-50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedDocuments.length === documents.length && documents.length > 0}
                  onChange={onSelectAll}
                  className="rounded border-border focus:ring-primary"
                />
              </th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Case</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Type</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Size</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Modified</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {documents.map(document => (
              <tr
                key={document.id}
                className={`hover:bg-secondary-50 transition-smooth ${
                  selectedDocuments.includes(document.id) ? 'bg-primary-50' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedDocuments.includes(document.id)}
                    onChange={() => onDocumentSelect(document.id)}
                    className="rounded border-border focus:ring-primary"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center">
                    <Icon name={getFileIcon(document.type)} size={20} className="text-primary mr-3" />
                    <div>
                      <div className="font-medium text-text-primary text-sm">{document.name}</div>
                      <div className="text-xs text-text-secondary">v{document.version}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-text-primary">{document.caseName}</div>
                  <div className="text-xs text-text-secondary">{document.caseId}</div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-secondary uppercase">{document.type}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-text-secondary">{formatFileSize(document.size)}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-text-secondary">{formatDate(document.lastModified)}</div>
                  <div className="text-xs text-text-secondary">by {document.modifiedBy}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(document.status)}`}>
                    {document.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onPreview(document)}
                      className="p-1 hover:bg-secondary-100 rounded transition-smooth"
                      title="Preview"
                    >
                      <Icon name="Eye" size={16} />
                    </button>
                    <button
                      onClick={() => onShare(document)}
                      className="p-1 hover:bg-secondary-100 rounded transition-smooth"
                      title="Share"
                    >
                      <Icon name="Share2" size={16} />
                    </button>
                    <button
                      onClick={() => onVersionHistory(document)}
                      className="p-1 hover:bg-secondary-100 rounded transition-smooth"
                      title="Version History"
                    >
                      <Icon name="History" size={16} />
                    </button>
                    <button className="p-1 hover:bg-secondary-100 rounded transition-smooth">
                      <Icon name="MoreVertical" size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {documents.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="mx-auto text-secondary-400 mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No documents found</h3>
          <p className="text-text-secondary">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default DocumentTable;